import React, {
	type FormEvent,
	useCallback,
	type ComponentPropsWithRef,
	type RefAttributes,
} from 'react';
import { styled as styled2 } from '@compiled/react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled, { type StyledComponentClass } from 'styled-components';
import { graphql, useFragment } from 'react-relay';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { main_softwareCodePage_FiltersUI$key } from '@atlassian/jira-relay/src/__generated__/main_softwareCodePage_FiltersUI.graphql';
import { FILTER_CONTAINER_TEST_ID } from '../../../../../../common/constants';
import {
	useAppliedPullRequestFilters,
	usePullRequestTableOptionsStore,
} from '../../../../../../controllers/pull-request-filters';
import { AuthorFilter } from './author-filter';
import messages from './messages';
import { RepositoriesFilter } from './repository-filter';
import { ReviewersFilter } from './reviewers-filter';
import { SearchFilter } from './search-filter';
import StatusFilter from './status-filter';

type FiltersUIProps = {
	disabled: boolean;
	filtersUiData: main_softwareCodePage_FiltersUI$key;
};

const formOnSubmit = (e: FormEvent<HTMLFormElement>) => {
	e.preventDefault();
};

const ClearFiltersButton = () => {
	const { formatMessage } = useIntl();
	const [, { resetFilters }] = usePullRequestTableOptionsStore();

	const onClick = useCallback(
		(event: React.MouseEvent, analyticsEvent: UIAnalyticsEvent) => {
			resetFilters();
			fireUIAnalytics(analyticsEvent, 'pullRequestTableClearFilters');
		},
		[resetFilters],
	);
	return (
		<StyledClearFiltersButton appearance="subtle" onClick={onClick}>
			{formatMessage(messages.clearFiltersLabel)}
		</StyledClearFiltersButton>
	);
};

export const FiltersUI = ({ disabled, filtersUiData }: FiltersUIProps) => {
	const data = useFragment(
		graphql`
			fragment main_softwareCodePage_FiltersUI on DevOpsPullRequestDetails @relay(plural: true) {
				...repositoryFilter_softwareCodePage_RepositoriesFilter
				...authorFilter_softwareCodePage
				...reviewersFilter_softwareCodePage
				...statusFilter_softwareCodePage
			}
		`,
		filtersUiData,
	);

	const [appliedFilters] = useAppliedPullRequestFilters();
	const hasAnyFilterApplied = Object.values(appliedFilters).some((value) => value === true);

	return (
		<FilterContainer data-testid={FILTER_CONTAINER_TEST_ID} onSubmit={formOnSubmit}>
			<SearchFilter disabled={disabled} />
			<RepositoriesFilter disabled={disabled} repositoriesFilterData={data} />
			<AuthorFilter disabled={disabled} authorFilterData={data} />
			<ReviewersFilter disabled={disabled} reviewersFilterData={data} />
			<StatusFilter disabled={disabled} statusFilterData={data} />
			{hasAnyFilterApplied && <ClearFiltersButton />}
		</FilterContainer>
	);
};

type ButtonProps = ComponentPropsWithRef<typeof Button>;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FilterContainer = styled2.form({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	margin: `${token('space.150', '12px')} 0`,
	flexWrap: 'wrap',
	gap: token('space.100', '8px'),
});

const StyledClearFiltersButton: StyledComponentClass<
	ButtonProps & RefAttributes<HTMLElement>,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	any
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
> = styled(Button)({
	marginLeft: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const visibleForTesting = {
	StyledClearFiltersButton,
};
